import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { StaffForm } from 'app/components/staff/StaffForm'
import { legacyRoleRank } from 'app/types/roles'
import type { RoleJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser, useWorkspace } from 'app/utils'
import type { OptionType } from 'types/OptionType'
import React from 'react'
import { graphql } from 'app/generated/gql'
import { useRoleStore } from 'app/stores/StoreContext'
import { gqlMutation } from 'app/lib/graphqlClient'

// The location selector renders one hidden `allowedLocationIds` input per location.
// The form collapses a single input to a string and multiple to an array, and omits
// it when none are picked. Coerce all three shapes to a string[] so the mutation always
// receives an array (TRK-5960 tightened this field and broke the one-location case).
export const inviteSubmitSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  roleId: z.string().optional(),
  staffId: z.string().optional(),
  allowedLocationIds: z.preprocess((v) => (Array.isArray(v) ? v : [v]), z.array(z.string())).optional(),
})

const CREATE_USER_MUTATION = graphql(`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      id
    }
  }
`)

const CREATE_WORKSPACE_INVITE_MUTATION = graphql(`
  mutation CreateWorkspaceInvite($input: CreateWorkspaceInviteInput!) {
    createWorkspaceInvite(input: $input) {
      id
    }
  }
`)

export type UseUserInviteReturn = {
  me: ReturnType<typeof useUser>
  currentWorkspace: ReturnType<typeof useWorkspace>
  workspaceRoles: RoleJson[]
  workspaceId: string | undefined
  isDirtyForm: boolean
  setIsDirtyForm: (v: boolean) => void
  role: number
  roleId: string
  selectedStaff: OptionType | undefined
  locationInputs: Array<{ name: string; value: string }>
  setLocationInputs: (v: Array<{ name: string; value: string }>) => void
  onRoleChange: (newRole: number | undefined, newRoleId: string | undefined) => void
  onStaffChange: (option: OptionType) => void
  openAddStaffModal: () => void
  placeholderUser: { id: string; allowedLocationIds: string[]; workspaceId: string | undefined }
  onSubmit: (body?: Record<string, unknown>) => void
}

export function useUserInvite(): UseUserInviteReturn {
  const me = useUser()

  const currentWorkspace = useWorkspace()
  const workspaceId = currentWorkspace?.id
  const navigate = useNavigate()
  const roleStore = useRoleStore()

  const [isDirtyForm, setIsDirtyForm] = useState(false)
  const [role, setRole] = useState<number>(legacyRoleRank.workspaceUser)
  const [roleId, setRoleId] = useState(``)
  const [selectedStaff, setSelectedStaff] = useState<OptionType | undefined>()
  const [locationInputs, setLocationInputs] = useState<Array<{ name: string; value: string }>>([])

  useEffect(() => {
    if (!workspaceId) return
    roleStore.fetchList({ pagination: { page: 0, limit: 1000 } })
  }, [workspaceId])

  const workspaceRoles = (roleStore.list?.hits ?? []) as RoleJson[]

  const openAddStaffModal = useCallback(() => {
    showModal(
      React.createElement(StaffForm, {
        hideAssociatedUser: true,
        onSubmit: (result: { staff?: { id?: string; name?: string } }) => {
          if (result.staff) {
            setSelectedStaff({ id: result.staff.id!, name: result.staff.name!, value: result.staff.id! })
            setIsDirtyForm(true)
          }
        },
      }),
      { title: `Add Staff`, isFullScreen: false, removePadding: true, useRadix: true },
    )
  }, [])

  // Reset roleId when workspace changes
  useEffect(() => {
    setRoleId(``)
  }, [workspaceId])

  const onRoleChange = useCallback((newRole: number | undefined, newRoleId: string | undefined) => {
    if (newRole !== undefined) setRole(newRole)
    if (newRoleId !== undefined) setRoleId(newRoleId)
  }, [])

  const onStaffChange = useCallback(
    (option: OptionType) => {
      if (selectedStaff && selectedStaff.id === option.id) {
        setSelectedStaff(undefined)
      } else if (option.id === `-1`) {
        setSelectedStaff(undefined)
      } else {
        setSelectedStaff(option)
      }
      setIsDirtyForm(true)
    },
    [selectedStaff],
  )

  const onSubmit = (body?: Record<string, unknown>) => {
    if (!body) return
    const parsed = inviteSubmitSchema.safeParse(body)
    if (!parsed.success) {
      notifyError(`Name, email and valid user settings are required`)
      return
    }
    const input = parsed.data
    gqlMutation(CREATE_USER_MUTATION, { input }).then(async (result) => {
      if (result.error) {
        notifyError(result.error.message)
        return
      }
      if (!result.data?.createUser?.id) {
        notifyError(`Failed to create user`)
        return
      }
      const inviteInput = {
        email: input.email,
        roleId: input.roleId || ``,
        staffId: input.staffId || undefined,
        locationIds: input.allowedLocationIds,
        code: uuidv4(),
        expiresAt: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
      }
      const invite = await gqlMutation(CREATE_WORKSPACE_INVITE_MUTATION, {
        input: inviteInput,
      })
      if (invite.error) {
        notifyError(`User created, but the invite failed: ${invite.error.message}`)
        return
      }
      notifySuccess(`User invited successfully`)
      navigate(`/settings/users/invited`)
    })
  }

  const placeholderUser = {
    id: `new`,
    allowedLocationIds: [] as string[],
    workspaceId,
  }

  return {
    me,
    currentWorkspace,
    workspaceRoles,
    workspaceId,
    isDirtyForm,
    setIsDirtyForm,
    role,
    roleId,
    selectedStaff,
    locationInputs,
    setLocationInputs,
    onRoleChange,
    onStaffChange,
    openAddStaffModal,
    placeholderUser,
    onSubmit,
  }
}
